// Pure derivations from raw API payloads. No side effects.

import { inRange } from './dateRange';

export const NEW_HOST_WINDOW_MS = 30 * 86400000;

export const PMS_OPTIONS = [
  { id: 'all', label: 'All PMS' },
  { id: 'guesty', label: 'Guesty' },
  { id: 'streamline', label: 'Streamline' },
  { id: 'hospitable', label: 'Hospitable' },
  { id: 'hostfully', label: 'Hostfully' },
  { id: 'track', label: 'Track' },
  { id: 'manual', label: 'Direct / Manual' },
  { id: 'unknown', label: 'Unknown' },
];

export const PMS_LABELS = {
  guesty: 'Guesty',
  hospitable: 'Hospitable',
  hostfully: 'Hostfully',
  track: 'Track',
  streamline: 'Streamline',
  manual: 'Direct / Manual',
  unknown: 'Unknown',
};

const normalizeLegacyPmsValue = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';
  if (normalized === 'other') return 'manual';
  return normalized;
};

const getLegacyPropertyManagementSoftware = (host) => {
  const raw = Array.isArray(host?.propertyManagementSoftware)
    ? host.propertyManagementSoftware
    : typeof host?.propertyManagementSoftware === 'string'
      ? [host.propertyManagementSoftware]
      : [];

  const normalized = raw
    .map(normalizeLegacyPmsValue)
    .filter(Boolean);

  return normalized[0] || '';
};

// Derive PMS for a host. Signal precedence:
// 1. backend-provided pmsType
// 2. explicit modern integration fields
// 3. legacy propertyManagementSoftware array
// 4. unknown
export function getHostPmsType(host) {
  if (!host) return 'unknown';
  const explicit = normalizeLegacyPmsValue(host.pmsType);
  if (explicit && explicit !== 'unknown') return explicit;
  if (host.trackConnectedAt || host.trackBaseUrl) return 'track';
  if (host.hostfullyConnectedAt || host.hostfullyAgencyUid || host.hostfullyAccessToken) {
    return 'hostfully';
  }
  if (host.hospitableConnectedAt || host.hospitableAccountId || host.hospitableAccessToken) {
    return 'hospitable';
  }
  if (host.guestyAccessToken || host.guesty_client_id) return 'guesty';
  if (host.streamline_token || host.streamline_token_secret) return 'streamline';
  const legacyPms = getLegacyPropertyManagementSoftware(host);
  if (legacyPms && PMS_LABELS[legacyPms]) return legacyPms;
  return 'unknown';
}

export const isHostActive = (host) => Boolean(host?.isVerified);
export const isHostNew = (host) => {
  if (!host?.createdAt) return false;
  return Date.now() - new Date(host.createdAt).getTime() < NEW_HOST_WINDOW_MS;
};

export function getHostName(host) {
  const full = `${host?.firstName || ''} ${host?.lastName || ''}`.trim();
  return full || host?.companyName || host?.email || 'Unnamed host';
}

export function getHostRefId(hostRef) {
  if (!hostRef) return '';
  if (typeof hostRef === 'string') return hostRef;
  if (typeof hostRef === 'object') {
    if (hostRef._id) return String(hostRef._id);
    if (hostRef.id) return String(hostRef.id);
  }
  return String(hostRef);
}

export function getPropertyHostId(property) {
  return getHostRefId(property?.hostID || property?.hostId || property?.host);
}

export function enrichProperty(property, hostById) {
  const hostId = getPropertyHostId(property);
  const host = hostId ? hostById?.get(hostId) : null;

  return {
    ...property,
    hostId,
    hostName: property?.hostName || (host ? getHostName(host) : '—'),
    hostEmail: property?.hostEmail || host?.email || '',
    hostPhoneNumber: property?.hostPhoneNumber || host?.phoneNumber || '',
    hostCompanyName: property?.hostCompanyName || host?.companyName || '',
  };
}

// Per-order revenue: sum all fee buckets minus discount.
// Falls back gracefully if any field is missing.
export function getOrderRevenue(o) {
  if (!o) return 0;
  const sum =
    Number(o.groceryFee || 0) +
    Number(o.driverFee || 0) +
    Number(o.bellhoptFee || 0) +
    Number(o.hostFee || 0) +
    Number(o.tip || 0) +
    Number(o.alcoholFee || 0) +
    Number(o.insuranceFee || 0) -
    Number(o.discount || 0);
  return Number.isFinite(sum) ? sum : 0;
}

export function countActivePropertiesFromOrders(orders) {
  const orderCountByPropertyId = new Map();

  orders.forEach((o) => {
    const propertyId = String(o?.propertyID || o?.property?._id || '');
    if (!propertyId) return;
    orderCountByPropertyId.set(propertyId, (orderCountByPropertyId.get(propertyId) || 0) + 1);
  });

  let activePropertyCount = 0;
  orderCountByPropertyId.forEach((count) => {
    if (count > 1) activePropertyCount += 1;
  });

  return activePropertyCount;
}

export const fmtMoney = (n) => {
  const v = Number(n) || 0;
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
};

export const fmtNumber = (n) => Number(n || 0).toLocaleString('en-US');

export const fmtCompact = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k`;
  return String(v);
};

// Build a derived host row used across the dashboard.
export function enrichHost(host) {
  const active = isHostActive(host);
  const isNew = isHostNew(host);
  return {
    ...host,
    pmsType: getHostPmsType(host),
    active,
    isNew,
    propertyCount: Number(host?.propertyCount || 0),
    name: getHostName(host),
  };
}

// Derive PMS distribution (Map<pmsType, hostCount>).
export function pmsDistribution(hosts) {
  const m = new Map();
  hosts.forEach((h) => {
    const k = h.pmsType || 'unknown';
    m.set(k, (m.get(k) || 0) + 1);
  });
  return m;
}

// Per-host order aggregates over a list of orders. Returns Map<hostId, {orders,revenue,aov}>.
// Order rows must already be hydrated with property.hostID via /orders endpoint.
export function aggregateOrdersByHost(orders, hostIdByPropertyId) {
  const m = new Map();
  orders.forEach((o) => {
    const propId = String(o.propertyID || o?.property?._id || '');
    const hostId =
      String(o?.property?.hostID || '') ||
      hostIdByPropertyId?.get(propId) ||
      '';
    if (!hostId) return;
    const cur = m.get(hostId) || { orders: 0, revenue: 0 };
    cur.orders += 1;
    cur.revenue += getOrderRevenue(o);
    m.set(hostId, cur);
  });
  m.forEach((v) => {
    v.aov = v.orders > 0 ? v.revenue / v.orders : 0;
  });
  return m;
}

// Filter hosts by current dashboard filter state.
export function filterHostsByState(enrichedHosts, state) {
  return enrichedHosts.filter((h) => {
    if (state.hostLifecycle === 'new' && !h.isNew) return false;
    if (state.hostLifecycle === 'legacy' && h.isNew) return false;
    if (state.pms !== 'all' && h.pmsType !== state.pms) return false;
    if (state.search) {
      const q = state.search.toLowerCase();
      const hay = `${h.name} ${h.email || ''} ${h.companyName || ''} ${h.phoneNumber || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

export function newlyOnboardedHosts(hosts, range) {
  return hosts.filter((h) => inRange(h.createdAt, range.start, range.end));
}

// --- Drivers ---
// Mongoose `{ timestamps: true }` on the Driver schema sets `createdAt` once
// at insert and never mutates it (only `updatedAt` updates), so we can treat
// `driver.createdAt` as the signup/acquisition timestamp.

export function getDriverName(d) {
  const full = `${d?.firstName || ''} ${d?.lastName || ''}`.trim();
  return full || d?.email || 'Unnamed driver';
}

export const isDriverActive = (d) =>
  Boolean(d?.isVerified && d?.isOnBoarded && (d?.checkrVerified ? d.checkrVerified === 'clear' : true));

export function enrichDriver(d) {
  return {
    ...d,
    name: getDriverName(d),
    active: isDriverActive(d),
    market: d?.market || d?.address?.city || '—',
  };
}

export function newlyAcquiredDrivers(drivers, range) {
  return drivers.filter((d) => inRange(d?.createdAt, range.start, range.end));
}
